import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '../ui/resizable';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Menu } from 'lucide-react';
import Sidebar from './Sidebar';
import SidebarRail from './SidebarRail';
import ContentArea from './ContentArea';
import FloatingActions from '../FloatingActions';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useIsMobile } from '../../hooks/use-mobile';
import { useAuth } from '../../contexts/AuthContext';
import LoginPage from '../LoginPage';

const SIDEBAR_MIN_SIZE = 15;
const SIDEBAR_MAX_SIZE = 35;

const DashboardLayout = () => {
  const { user, loading } = useAuth();
  const isMobile = useIsMobile();
  // Сохраняем размер сайдбара в localStorage
  const [sidebarSize, setSidebarSize] = useLocalStorage('sidebar-size', 20);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage('sidebar-collapsed', false);
  const sidebarPanelRef = useRef(null);
  const [isHandleDragging, setIsHandleDragging] = useState(false);
  const hideHandle = sidebarCollapsed && !isHandleDragging;
  const railExpandButtonRef = useRef(null);
  const sidebarCollapseButtonRef = useRef(null);
  // Кнопка, которую нажали, исчезает (rail размонтируется / панель становится inert),
  // поэтому фокус переносим на парную кнопку — только при переключении кнопкой
  const focusAfterToggleRef = useRef(null);

  const collapseSidebar = useCallback(() => {
    focusAfterToggleRef.current = railExpandButtonRef;
    setSidebarCollapsed(true);
  }, [setSidebarCollapsed]);

  const expandSidebar = useCallback(() => {
    focusAfterToggleRef.current = sidebarCollapseButtonRef;
    setSidebarCollapsed(false);
  }, [setSidebarCollapsed]);

  // Обработчик изменения размера панелей. Свёрнутая панель (0%) не перезаписывает
  // сохранённую ширину — к ней панель возвращается при разворачивании
  const handleLayout = useCallback((sizes) => {
    if (sizes && sizes.length > 0 && sizes[0] > 0) {
      setSidebarSize(sizes[0]);
    }
  }, [setSidebarSize]);

  // Источник правды — sidebarCollapsed; панель подтягивается к нему. Дерево
  // компонентов при этом не меняется, иначе ContentArea перемонтировался бы
  // и все виджеты заново грузили данные
  // useLayoutEffect — чтобы rail и ширина панели менялись в одном кадре, без мигания
  useLayoutEffect(() => {
    const panel = sidebarPanelRef.current;
    if (panel) {
      if (sidebarCollapsed && !panel.isCollapsed()) {
        panel.collapse();
      } else if (!sidebarCollapsed && panel.isCollapsed()) {
        // Ширина вне [15, 35] (чужое значение в localStorage) библиотекой молча
        // игнорируется — панель осталась бы нулевой при развёрнутом состоянии
        panel.resize(Math.min(Math.max(sidebarSize, SIDEBAR_MIN_SIZE), SIDEBAR_MAX_SIZE));
      }
    }
    const focusTarget = focusAfterToggleRef.current;
    focusAfterToggleRef.current = null;
    // preventScroll: в момент фокуса панель ещё нулевой ширины, браузер иначе
    // прокрутил бы её содержимое вбок
    focusTarget?.current?.focus({ preventScroll: true });
  }, [sidebarCollapsed, sidebarSize]);

  const closeMobileSidebar = useCallback(() => setMobileSidebarOpen(false), []);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <LoginPage />;
  }

  // На телефонах сайдбар не влезает в resizable-панель (15% от 375px — это ~56px),
  // поэтому он уезжает в выдвижную панель, а контент занимает всю ширину
  if (isMobile) {
    return (
      <div className="h-full w-full flex flex-col relative">
        <header className="flex-shrink-0 flex items-center gap-2 h-14 px-3 border-b border-border bg-sidebar">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={() => setMobileSidebarOpen(true)}
            aria-label="Открыть фильтры"
          >
            <Menu className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium truncate">Фильтры и разделы</span>
        </header>

        <div className="flex-1 min-h-0 bg-background overflow-y-auto">
          <ContentArea />
        </div>

        <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
          <SheetContent side="left" className="w-[88vw] max-w-sm p-0 gap-0 bg-sidebar">
            <SheetTitle className="sr-only">Фильтры и разделы</SheetTitle>
            <div className="h-full pt-10">
              <Sidebar onNavigate={closeMobileSidebar} />
            </div>
          </SheetContent>
        </Sheet>

        <FloatingActions />
      </div>
    );
  }

  return (
    <div className="h-full w-full relative flex">
      {sidebarCollapsed && <SidebarRail onExpand={expandSidebar} expandButtonRef={railExpandButtonRef} />}
      <div className="flex-1 min-w-0 h-full">
        <ResizablePanelGroup
          direction="horizontal"
          className="h-full"
          onLayout={handleLayout}
        >
          {/* collapsible: перетаскивание края до упора влево тоже сворачивает панель */}
          <ResizablePanel
            ref={sidebarPanelRef}
            defaultSize={sidebarCollapsed ? 0 : sidebarSize}
            minSize={SIDEBAR_MIN_SIZE}
            maxSize={SIDEBAR_MAX_SIZE}
            collapsible
            collapsedSize={0}
            onCollapse={() => setSidebarCollapsed(true)}
            onExpand={() => setSidebarCollapsed(false)}
          >
            {/* Свёрнутая панель остаётся смонтированной (не теряются невыбранные фильтры),
                inert не даёт Tab уйти в невидимые контролы */}
            <div className="h-full bg-sidebar border-r border-border" inert={sidebarCollapsed ? '' : undefined}>
              <Sidebar onCollapse={collapseSidebar} collapseButtonRef={sidebarCollapseButtonRef} />
            </div>
          </ResizablePanel>
          {/* Ручка отключается только после отпускания мыши: отключение посреди
              перетаскивания оставляет библиотеку в состоянии «кнопка зажата», и следующий
              захват не срабатывает. Без disabled скрытая ручка ловила бы клики
              в левом верхнем углу — её прямоугольник схлопывается в точку (0,0) */}
          <ResizableHandle
            withHandle
            onDragging={setIsHandleDragging}
            disabled={hideHandle}
            className={hideHandle ? 'hidden' : undefined}
          />
          <ResizablePanel defaultSize={sidebarCollapsed ? 100 : 100 - sidebarSize}>
            <div className="h-full bg-background overflow-y-auto">
              <ContentArea />
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Плавающие кнопки */}
      <FloatingActions />
    </div>
  );
};

export default DashboardLayout;
